// ============================================================
// PERRY — APP
// The router and the ONLY place rewards are granted. Every screen
// is a string; every reward path runs GRANT-BEFORE-FLAG:
//   1. write the reward (kindling / crests / milestone / achievement)
//   2. only then write the flag that says it was given
// A crash between them costs a duplicate grant, never a lost one.
//
// Auth: onAuthStateChanged is the truth. The ?uid= arriving from
// Unstable Pierre is read once, stripped from the URL immediately,
// and used only as a hint on the login card.
// ============================================================

import { useState, useEffect, useCallback, useRef } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "./firebase";

import BootSequence from "./BootSequence";
import LoginOverlay from "./LoginOverlay";
import MainMenu from "./MainMenu";
import SpeciesSelect from "./SpeciesSelect";
import RunScreen from "./RunScreen";
import VillageScene from "./VillageScene";
import Customize from "./Customize";
import Ascension from "./Ascension";
import PerryCodex from "./PerryCodex";
import OptionsMenu from "./OptionsMenu";
import AchievementToast from "./AchievementToast";

import { GM } from "./theme";
import { ensurePerryUIKeyframes } from "./PerryUI";
import { grantAchievement } from "./achievementWrites";
import { getAchievement } from "./achievements";
import {
  RULES, TREASURES, GLYPH, CREST_BOUNTY, deriveStanding, latestAct,
  // ── 2026-07-30 ── the Sheathe
  RULES_ARM, armProgress,
} from "./perryData";
import {
  listenToSoul, listenToCrests, listenToKindling, forgeSoul, takeNode, getSoul,
  buyAscension, migrateRenown,
  growChao, grantMilestone, grantTome, consumeTome, grantCurio,
  grantTreasure, equipTreasure, setFinish, setSlots,
  awardCrests, spendCrests, awardKindling, spendKindling,
  migrateCrestsToKindling, recordPurchase, hasPurchased,
  recordRun, getRecords,
  // ── 2026-07-30 ── the Sheathe's ledger
  buyArmRung, reforgeTo, beginAwakening, awakenProgress, crownArm,
  readUidHint, goToUnstablePierre,
} from "./perryProgression";

export default function App() {
  ensurePerryUIKeyframes();

  const [screen, setScreen] = useState("boot");
  const [uid, setUid] = useState(null);
  const [authReady, setAuthReady] = useState(false);
  const [uidHint] = useState(readUidHint);          // read + stripped once, at mount
  const [soul, setSoul] = useState(null);
  const [soulReady, setSoulReady] = useState(false);
  const [crests, setCrests] = useState(0);      // ◆ the Empire's reserve
  const [kindling, setKindling] = useState(0);  // ⋔ ours
  const [records, setRecords] = useState(null);
  const [toastIds, setToastIds] = useState([]);
  const [lastRun, setLastRun] = useState(null);
  const [runSeed, setRunSeed] = useState(0);   // remounts RunScreen for a fresh walk
  const [arrivedNow, setArrivedNow] = useState(false);
  // Where KIT UP was entered FROM. Back used to guess from the
  // hollowmere milestone, which was right while the only door was
  // the village — now that the main menu opens the same screen,
  // guessing sends a menu visitor to Hollowmere instead of back.
  // Remember the door instead of inferring it.
  const [kitFrom, setKitFrom] = useState("menu");

  // Renown accrues in a ref during a run and is written ONCE at the
  // end — a transaction per parry would hammer the database and
  // stutter the loop. The run is the unit of truth, not the beat.
  // ── RETIRED 2026-07-29 ── `pendingRenown` is gone with Renown
  // itself. The road's pay banks in the director's own stats and
  // settles as ⋔ in one transaction, exactly as it always did —
  // there was never a second number, only a second name for it.
  const pendingKitups = useRef(0);
  const renownMigrated = useRef(false);
  const pendingGrowth = useRef(0);

  const toast = useCallback((ids) => {
    if (!ids || !ids.length) return;
    setToastIds((prev) => [...prev, ...ids]);
  }, []);

  // ── AUTH ───────────────────────────────────────────────────
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => {
      setUid(user ? user.uid : null);
      setAuthReady(true);
    });
    return () => unsub();
  }, []);

  // ── NARROW LISTENERS — leaves only, never the profile root ──
  useEffect(() => {
    if (!uid) {
      setSoul(null); setSoulReady(false);
      setCrests(0); setKindling(0); setRecords(null);
      return undefined;
    }
    setSoulReady(false);
    // ── THE ONE-TIME CARRY-ACROSS ──
    // Fire-and-forget, before the listeners settle. It is idempotent
    // and it no-ops on every account that has already run it, which
    // after the first session is all of them. The listeners below
    // pick up whatever it moves, so nothing here has to await it.
    migrateCrestsToKindling(uid).catch(() => {});
    // ── AND THE SECOND ONE, 2026-07-29 ──
    // Renown → Kindling + paid-up Ascensions. Unlike the crest
    // carry-across this one needs the SOUL in hand (it has to know
    // how many stars are already lit), so it hangs off the first
    // listener tick and guards itself with a ref against the
    // listener firing again a millisecond later. Idempotent twice
    // over — the ref stops the double call, the Firebase flag stops
    // the double payout.
    const offSoul = listenToSoul(uid, (s) => {
      setSoul(s);
      setSoulReady(true);
      if (s && !renownMigrated.current) {
        renownMigrated.current = true;
        migrateRenown(uid, s).catch(() => {});
      }
    });
    const offCrests = listenToCrests(uid, setCrests);
    const offKindling = listenToKindling(uid, setKindling);
    getRecords(uid).then(setRecords).catch(() => setRecords(null));
    return () => { offSoul(); offCrests(); offKindling(); };
  }, [uid]);

  // ══════════════════════════════════════════════════════════
  //  BUYING AN ASCENSION — the other half of the merge
  // ══════════════════════════════════════════════════════════
  // Stars used to be free once you had the standing. They cost ⋔
  // now, from the same purse the weapon rack draws on, and that
  // competition IS the feature: every point is an axe you did not
  // buy. Legality is checked in Customize against the live purse;
  // this is the write, and it is the only one.
  const onAscend = useCallback(async (n = 1) => {
    if (!uid) return false;
    return buyAscension(uid, n);
  }, [uid]);

  // ══════════════════════════════════════════════════════════
  //  THE SHEATHE'S THREE ACTIONS — 2026-07-30
  // ══════════════════════════════════════════════════════════
  // App owns every write in this title and these are no exception.
  // The Ascension screen decides WHAT is legal (it has the derived
  // state); this decides whether it HAPPENS.
  const onAscendArm = useCallback(async (treasureId, rungKey, cost) => {
    if (!uid) return false;
    const ok = await buyArmRung(uid, treasureId, rungKey, cost);
    if (!ok) return false;
    // THE CROWN IS NOT AN ACHIEVEMENT YET — lighting every rung
    // makes a weapon ELIGIBLE, and eligibility is not the thing
    // caP'n wanted rewarded. `perry:branch_ascended` reads `lord`,
    // and lord is earned in the fens, not bought at a table.
    return true;
  }, [uid]);

  const onReforge = useCallback(async (treasureId, cost) => {
    if (!uid) return false;
    return reforgeTo(uid, treasureId, cost);
  }, [uid]);

  const onAwaken = useCallback(async (treasureId) => {
    if (!uid) return false;
    return beginAwakening(uid, treasureId);
  }, [uid]);

  const refreshRecords = useCallback(() => {
    if (uid) getRecords(uid).then(setRecords).catch(() => {});
  }, [uid]);

  // ── ACHIEVEMENT HELPER — grant, then surface ───────────────
  const award = useCallback(async (achId, source = "") => {
    if (!uid) return;
    try {
      const res = await grantAchievement(uid, achId, source);
      if (res?.granted?.length) {
        toast(res.granted);
        // ── THE ONE-TIME ◆ FAUCET, 2026-07-28 ──
        // Only the achievements that were ACTUALLY newly granted
        // pay. res.granted is the fresh ones — a re-grant of
        // something already owned returns an empty list and pays
        // nothing, which is what makes this idempotent and what
        // makes it legal under the Faucet Law.
        //
        // Meta achievements ride along in the same list, so the
        // Empire tier (Two Worlds, Citizen) pays here too, once,
        // without this call site knowing they exist.
        // res.granted is a list of IDs, not objects — the rarity
        // is looked up from the shared registry so this stays right
        // when a rarity is retuned in achievements.js.
        let bounty = 0;
        for (const id of res.granted) {
          bounty += CREST_BOUNTY[getAchievement(id)?.rarity] || 0;
        }
        if (bounty > 0) await awardCrests(uid, bounty);
      }
    } catch (e) {
      console.warn("[perry] achievement grant failed", achId, e);
    }
  }, [uid, toast]);

  // ── RUN EVENTS — the director speaks, App writes ───────────
  const onRunEvent = useCallback(async (type, payload) => {
    if (!uid) return;
    // ── KIT UP ── flavour + a counter for the end card. No write:
    // a fire on the road is not a reward, it is a place.
    if (type === "kitup") { pendingKitups.current += 1; return; }
    if (type === "growth") { pendingGrowth.current += 1; return; }
    if (type === "consumeTome") { await consumeTome(uid, payload); return; }
    // ── THE SHEATHE REVEALS ITSELF — once per Soul ──
    // The director has already started speaking; this is the flag
    // that stops it ever speaking again. Deliberately NOT
    // grant-before-flag, because there is no grant — the reward
    // IS the scene, and the scene has already been given by the
    // time this line runs. A crash here costs a replay of a good
    // scene, which is the cheapest failure in the file.
    if (type === "sheathe") { await grantMilestone(uid, "sheathe"); return; }
    // ══════════════════════════════════════════════════════════
    //  A RUIN — the director names the foe, App names the brag
    // ══════════════════════════════════════════════════════════
    // The director fires this on every kill and knows nothing
    // about achievements, which is correct and is how it has
    // always been. The ⋔ for the Ruin was already banked in the
    // run's purse and settles at the end — nothing is written
    // here but the ledger.
    //
    // Th'rakk is the only entry today. caP'n, 2026-07-30: the
    // Ridge Gate's warden "was tough, you weren't meant to beat
    // it" — the tutorial fight resolves perfectly well by simply
    // SURVIVING it, and it will, because the beats run out. Going
    // the other way and actually breaking a Champion's wall with
    // a fresh beb's edge is the brag, and it is really a New Game
    // Plus brag. Legendary, and it pays 4 ◆ through CREST_BOUNTY
    // like every other one-time source in the title.
    //
    // A list would have been wrong here: the next boss worth an
    // entry costs one line in this switch, not a new event type.
    if (type === "ruin") {
      if (payload === "chain_warden") await award("perry:thrakk", "act1");
      return;
    }
    if (type === "parry") {
      // cheap, once-only achievement checks
      await award("perry:first_parry", "run");
      if (payload === "PERFECT") await award("perry:first_perfect", "run");
      return;
    }
    if (type === "chain") {
      // GRANT-BEFORE-FLAG: the purse first, the achievement second.
      // ⋔ and not ◆ — a chain is repeatable by definition, and the
      // Faucet Law has no interest in how hard it was.
      const amount = payload === 10 ? RULES.KINDLING_CHAIN_10
        : payload === 30 ? RULES.KINDLING_CHAIN_30
          : payload === 50 ? RULES.KINDLING_CHAIN_50 : 0;
      if (amount) await awardKindling(uid, amount);
      await award(`perry:chain_${payload}`, "chain");
    }
  }, [uid, award]);

  // ── RUN END — one write pass, in reward-then-flag order ────
  const settleRun = useCallback(async (how, stats) => {
    if (!uid) return;
    const growth = pendingGrowth.current;
    const fires = pendingKitups.current;
    pendingGrowth.current = 0;
    pendingKitups.current = 0;

    try {
      for (let i = 0; i < growth; i++) await growChao(uid);

      // ── THE ROAD'S OWN PAY, 2026-07-27 ──
      // Banked per answer by the director, settled HERE in ONE
      // delta transaction — before the milestone and before the
      // achievements, because grant-before-flag is not optional and
      // a crash between them must cost a duplicate grant, never a
      // lost one.
      //
      // It runs on BOTH exits. That is the feature: a player who
      // keeps dying on the ridge still walks away with the ⋔ that
      // buys the Treasure that gets them past it. Falling costs you
      // the walk and nothing else — it always did, and now the
      // walk pays for itself.
      const earned = stats?.kindling || 0;
      if (earned > 0) await awardKindling(uid, earned);

      // ══════════════════════════════════════════════════════════
      //  THE AWAKENING SETTLES — and may crown (2026-07-30)
      // ══════════════════════════════════════════════════════════
      // Banked per Ruin by the director, settled here in ONE
      // transaction. Then, if the mission is complete, the crown
      // goes on — GRANT BEFORE FLAG, which `crownArm` does in the
      // right order internally: lord first, mission closed second.
      //
      // It reads the count back from the DATABASE rather than
      // trusting the local total, because a player can walk the
      // road on two devices and the leaf transaction is the only
      // thing that knows the real number.
      const heldId = soul?.treasure || "t_reed_switch";
      if ((stats?.awakenRuins || 0) > 0) {
        await awakenProgress(uid, heldId, stats.awakenRuins);
        const after = await getSoul(uid);
        const prog = armProgress(after, heldId);
        if (prog.awakening && (prog.awakening.ruins || 0) >= RULES_ARM.AWAKEN_RUINS) {
          await crownArm(uid, heldId);
          if (heldId === "t_reed_switch") await award("perry:branch_ascended", "sheathe");
        }
      }

      await recordRun(uid, {
        ...stats,
        kitups: fires,
        arrived: how === "arrived",
        fell: how === "fell",
        clearedAs: how === "arrived" ? soul?.species : null,
      });

      if (how === "arrived") {
        // 1 — the rewards
        await awardKindling(uid, RULES.KINDLING_ARRIVE);
        // 2 — the flag that says they were given
        await grantMilestone(uid, "hollowmere");
        // 3 — the ledger
        await award("perry:reach_hollowmere", "act1");
        if (stats.perfects > 0 && stats.parries > 0 && stats.bestChain === stats.parries) {
          await award("perry:no_miss_act1", "act1");
        }
        // ══════════════════════════════════════════════════════
        //  THE REFUSALS — R-54, on arrival only
        // ══════════════════════════════════════════════════════
        // Each reads a flag the director set at the SPEND, never a
        // fact about the Soul at the finish. `no_treasures` is the
        // one exception and it is deliberately a Soul read: not
        // buying anything is a thing you did across the whole
        // account, not a thing you did on one walk, and the ledger
        // is what remembers it.
        //
        // The reed is free and implicit, so an empty `treasures`
        // map IS the empty-handed condition — a beb who owns
        // nothing still walked out of Reedbrook holding a branch.
        // ══════════════════════════════════════════════════════
        //  A SKIPPED ROAD REFUSES NOTHING — 2026-07-30
        // ══════════════════════════════════════════════════════
        // Caught while wiring the ESC door, not reported by
        // anyone. Every refusal below is a flag that starts TRUE
        // and only ever goes false when you do the thing — so a
        // skipped Tutorial, which is an arrival with zero parries
        // and zero Arts spoken, would have handed out `untouched`
        // and `no_arts` for pressing Escape twice.
        //
        // The refusals are brags about a walk. Leaving the road
        // is not a walk. `no_treasures` reads the Soul rather
        // than the run and would have been "safe" either way, but
        // it lives in the same block and the block is what has
        // the meaning — all four go, or none do.
        const armState = soul?.arms?.[soul?.treasure || "t_reed_switch"];
        if (!stats?.skipped) {
          if (stats?.pureUntouched) await award("perry:untouched", "act1");
          if (stats?.pureNoArts) await award("perry:no_arts", "act1");
          if (!Object.keys(soul?.treasures || {}).length
            && !Object.keys(soul?.tomes || {}).length
            && !Object.keys(soul?.curios || {}).length) {
            await award("perry:no_treasures", "act1");
          }
          if ((soul?.treasure || "t_reed_switch") === "t_reed_switch") {
            // Never Ascended it once — the hardest of the six, and
            // the only one that gets harder the longer you play.
            if (!armState || !Object.keys(armState.rungs || {}).length) {
              await award("perry:never_upgraded", "act1");
            }
            if (armState?.lord) await award("perry:branch_champion", "sheathe");
          }
        }

        const fresh = await getRecords(uid);
        setRecords(fresh);
        const clears = fresh?.clears || {};
        if (["h01", "shadow_fox", "antdroid", "chaos_dragon"].every((k) => clears[k])) {
          await award("perry:all_four", "act1");
        }
      } else {
        refreshRecords();
      }
    } catch (e) {
      console.warn("[perry] settle failed", e);
    }
  }, [uid, soul, award, refreshRecords]);

  // ── SCREEN ACTIONS ─────────────────────────────────────────
  const startNewGame = () => setScreen("species");

  const chooseSpecies = async (species) => {
    if (!uid) return;
    await forgeSoul(uid, species);      // the ONE intentional wipe
    setLastRun(null);
    setArrivedNow(false);
    setScreen("run");
  };

  const onFell = (stats) => {
    setLastRun(stats);
    settleRun("fell", stats);
    setRunSeed((n) => n + 1);
    // Falling before Hollowmere sends you to the menu; after it, home
    // to the village. The only fail state is the fail state — nothing
    // you earned on the way down is taken back.
    setScreen(soul?.milestones?.hollowmere ? "village" : "menu");
  };

  const onArrived = (stats) => {
    setLastRun(stats);
    settleRun("arrived", stats);
    setArrivedNow(true);
    setScreen("village");
  };

  const onEquip = async (treasureId) => {
    if (!uid || !TREASURES[treasureId]) return;
    await equipTreasure(uid, treasureId);
  };

  const onFinish = async (finishId) => {
    if (!uid || !finishId) return;
    await setFinish(uid, finishId);
  };

  // ── THE GRIMOIRE, 2026-07-28 ───────────────────────────────
  // Which five powers ride on keys 1-5. A preference, like the
  // Treasure in his paws and the colour it burns — no cost, no
  // gate, no achievement, and reversible in one click forever.
  // Kit Up hands over the whole rack; this writes it as one leaf.
  const onSlots = async (next) => {
    if (!uid || !Array.isArray(next)) return;
    await setSlots(uid, next);
  };

  const onTakeNode = async (node) => {
    if (!uid) return;
    await takeNode(uid, node);
    await award("perry:first_ascend", "constellation");
  };

  // ── THE COUNTER, 2026-07-28 — two lanes, one function ──────
  // Prices are now DATA, not a branch. `item.price` names its own
  // currency ({ kindling: 40 } · { dc: 8 } · { anima: 2 }) and this
  // reads whichever one is there. A third currency is a row in
  // PAY, not an edit to this function — which is the entire reason
  // it is written this way and not as an if/else over two names.
  const PAY = {
    kindling: { spend: spendKindling, glyph: GLYPH.kd, name: "Kindling" },
    dc: { spend: spendCrests, glyph: GLYPH.dc, name: "Divine Crests" },
  };

  const onBuy = async (id, kind, item) => {
    if (!uid) return { message: "Not signed in." };

    // ── THE BLOOD BARGAIN — priced in Anima, paid at the counter.
    // Nothing to deduct now: the tome takes its 2 Anima off your
    // ceiling for the run it saves you in. Whyllie keeps books.
    if (item.price?.anima) {
      await grantTome(uid, id);
      return { message: "Whyllie takes your paw and does not say what he takes with it. The tome is yours." };
    }

    const lane = Object.keys(PAY).find((k) => item.price?.[k] != null);
    const cost = lane ? item.price[lane] : 0;
    const pay = lane ? PAY[lane] : null;

    // ── ONCE PER ACCOUNT — the ◆ top shelf ──
    // Already on the ledger? It is handed back, free. See
    // recordPurchase() — the Soul burns, the receipt does not.
    let free = false;
    if (item.oncePerAccount) free = await hasPurchased(uid, id);

    if (!free && pay && cost > 0) {
      const paid = await pay.spend(uid, cost);
      if (!paid) {
        return { message: `Not enough ${pay.name}. You need ${pay.glyph} ${cost}.` };
      }
      if (item.oncePerAccount) await recordPurchase(uid, id);
    }

    // GRANT BEFORE FLAG, as always: the thing is written first and
    // only then equipped. A crash between them costs a menu click,
    // never the thing you paid for.
    if (kind === "treasure") {
      await grantTreasure(uid, id);
      await equipTreasure(uid, id);
      return {
        message: free
          ? `Whyllie checks a book, finds your name in it, and says nothing at all. — ${item.name} is in your paws.`
          : `“Mind the sharp bit, tiny customer.” — ${item.name} is in your paws.`,
      };
    }
    if (kind === "tome") await grantTome(uid, id);
    else await grantCurio(uid, id);
    return { message: `“Pleasure doing business, tiny customer.” — ${item.name} acquired.` };
  };

  const toPierre = () => {
    if (!goToUnstablePierre(uid)) setScreen("menu");
  };

  // ── RENDER ─────────────────────────────────────────────────
  if (screen === "boot") {
    return (
      <Frame>
        <BootSequence onStart={() => setScreen(uid ? "menu" : "login")} />
      </Frame>
    );
  }

  if (!authReady) return <Frame><Loading text="WAKING THE EMBER" /></Frame>;

  if (!uid) {
    return (
      <Frame>
        <LoginOverlay uidHint={uidHint} />
      </Frame>
    );
  }

  if (!soulReady) return <Frame><Loading text="READING YOUR SOUL" /></Frame>;

  return (
    <Frame>
      {screen === "menu" && (
        <MainMenu
          soul={soul}
          crests={crests}
          kindling={kindling}
          uid={uid}
          standing={soul ? deriveStanding(soul) : ""}
          onNewGame={startNewGame}
          onContinue={() => { setArrivedNow(false); setScreen(soul?.milestones?.hollowmere ? "village" : "run"); }}
          onCodex={() => setScreen("codex")}
          onCustomize={() => { setKitFrom("menu"); setScreen("customize"); }}
          onOptions={() => setScreen("options")}
          onPierre={toPierre}
        />
      )}

      {screen === "species" && (
        <SpeciesSelect onChoose={chooseSpecies} onBack={() => setScreen("menu")} />
      )}

      {screen === "run" && soul && (
        <RunScreen
          key={`${soul.createdAt}:${runSeed}:${latestAct(soul)}`}
          soul={soul}
          actId={latestAct(soul)}
          onEvent={onRunEvent}
          onFell={onFell}
          onArrived={onArrived}
        />
      )}

      {screen === "village" && soul && (
        <VillageScene
          soul={soul}
          crests={crests}
          kindling={kindling}
          arrivedNow={arrivedNow}
          runSummary={lastRun}
          onBuy={onBuy}
          onCustomize={() => { setKitFrom("village"); setScreen("customize"); }}
          onCodex={() => setScreen("codex")}
          onReplay={() => { setArrivedNow(false); setRunSeed((n) => n + 1); setScreen("run"); }}
          onMenu={() => { setArrivedNow(false); setScreen("menu"); }}
        />
      )}

      {screen === "customize" && soul && (
        <Customize
          soul={soul}
          crests={crests}
          kindling={kindling}
          onTake={onTakeNode}
          onAscend={onAscend}
          onEquip={onEquip}
          onFinish={onFinish}
          onSlots={onSlots}
          onBuy={onBuy}
          onAscension={() => setScreen("ascension")}
          onBack={() => setScreen(kitFrom === "village" ? "village" : "menu")}
        />
      )}

      {/* ── THE ASCENSION — the Sheathe's screen, 2026-07-30 ──
          Full width, its own route, deliberately not a fifth tab
          in Customize: everything there is a LIST of things you
          own, and this is a LADDER for one object with a character
          talking over it. Returns wherever it was opened from. */}
      {screen === "ascension" && soul && (
        <Ascension
          soul={soul}
          kindling={kindling}
          onAscendArm={onAscendArm}
          onReforge={onReforge}
          onAwaken={onAwaken}
          onBack={() => setScreen(kitFrom === "village" ? "village" : "menu")}
        />
      )}

      {screen === "codex" && (
        <PerryCodex
          uid={uid}
          records={records}
          soul={soul}
          onBack={() => setScreen(soul?.milestones?.hollowmere && arrivedNow ? "village" : "menu")}
        />
      )}

      {screen === "options" && <OptionsMenu onBack={() => setScreen("menu")} />}

      {/* the toast rides at the root so it survives every screen change */}
      <AchievementToast ids={toastIds} onDrained={() => setToastIds([])} />
    </Frame>
  );
}

// ── Chrome ───────────────────────────────────────────────────
function Frame({ children }) {
  return (
    <div style={{
      position: "fixed", inset: 0, background: "#050302",
      color: GM.vellum, overflow: "hidden",
    }}>
      {children}
    </div>
  );
}

function Loading({ text }) {
  return (
    <div style={{
      position: "absolute", inset: 0, display: "flex",
      alignItems: "center", justifyContent: "center",
      background: "#050302", fontFamily: GM.fontUI,
    }}>
      <div style={{
        fontSize: 11, letterSpacing: 7, color: GM.vellumFaint,
        animation: "perryPulse 1.5s ease-in-out infinite",
      }}>{text}</div>
    </div>
  );
}
